import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

/**
 * Build the redirect URL after login.
 */
function buildRedirectUrl(redirect: string, accessToken: string): string {
  try {
    const url = new URL(redirect);
    const isLocalInstance = url.hostname.endsWith('.localhost') && url.hostname.startsWith('claw-');

    if (isLocalInstance) {
      const targetPath = url.pathname + url.search;
      return `${url.origin}/_auth?token=${encodeURIComponent(accessToken)}&redirect=${encodeURIComponent(targetPath)}`;
    }

    return redirect;
  } catch {
    return redirect;
  }
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const { signIn, signUp, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectUrl = searchParams.get('redirect');

  useEffect(() => {
    if (authLoading) return;

    if (session?.access_token && redirectUrl) {
      const targetUrl = buildRedirectUrl(redirectUrl, session.access_token);
      window.location.href = targetUrl;
    } else if (session && !redirectUrl) {
      navigate('/');
    }
  }, [session, authLoading, redirectUrl, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      if (isSignUp) {
        const { error } = await signUp(email, password);
        if (error) {
          setError(error.message);
        } else {
          setMessage('Check your email for a confirmation link!');
        }
      } else {
        const { error, session } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          if (redirectUrl && session?.access_token) {
            const targetUrl = buildRedirectUrl(redirectUrl, session.access_token);
            window.location.href = targetUrl;
          } else {
            navigate('/');
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-emoji">🤖</div>
          <h1>OpenClaw</h1>
          <p className="subtitle">
            {isSignUp ? 'Create your account to get started' : 'Welcome back! Sign in to continue'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              minLength={6}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                {isSignUp ? 'Creating account...' : 'Signing in...'}
              </>
            ) : (
              isSignUp ? '✨ Create Account' : '🚀 Sign In'
            )}
          </button>
        </form>

        <div className="toggle-mode">
          {isSignUp ? (
            <p>
              Already have an account?{' '}
              <button type="button" onClick={() => setIsSignUp(false)}>
                Sign In
              </button>
            </p>
          ) : (
            <p>
              Don't have an account?{' '}
              <button type="button" onClick={() => setIsSignUp(true)}>
                Sign Up
              </button>
            </p>
          )}
        </div>
      </div>

      {/* Decorative elements */}
      <div className="login-decoration">
        <div className="decoration-circle circle-1"></div>
        <div className="decoration-circle circle-2"></div>
        <div className="decoration-circle circle-3"></div>
      </div>
    </div>
  );
}
